// Quiz Dashboard Component - Lists and manages quizzes
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-hot-toast";
import { Quiz } from "../../../types/quiz";
import quizService from "../../../services/quizService";
import AvailabilityStatus from "./AvailabilityStatus";

interface QuizDashboardProps {
  courseId?: string;
  userType: "teacher" | "student";
}

const QuizDashboard: React.FC<QuizDashboardProps> = ({
  courseId,
  userType,
}) => {
  const [quizzes, setQuizzes] = useState<Quiz[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<
    "all" | "published" | "draft" | "archived"
  >("all");

  const fetchQuizzes = async () => {
    try {
      setLoading(true);
      const params = {
        page,
        limit: 10,
        ...(courseId && { courseId }),
        ...(searchTerm && { search: searchTerm }),
        ...(statusFilter !== "all" && {
          status: statusFilter as "published" | "draft" | "archived",
        }),
      };

      const data =
        userType === "teacher"
          ? await quizService.getTeacherQuizzes(params)
          : await quizService.getStudentQuizzes(params);

      // Handle different response formats
      if (userType === "teacher" && "quizzes" in data) {
        setQuizzes(data.quizzes);
        setTotalPages(data.totalPages);
      } else {
        setQuizzes(data as Quiz[]);
        setTotalPages(1); // No pagination for student quizzes
      }
    } catch (error: any) {
      console.error("Error fetching quizzes:", error);
      toast.error("Failed to load quizzes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQuizzes();
  }, [page, searchTerm, statusFilter, courseId, userType]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    fetchQuizzes();
  };

  const handleDeleteQuiz = async (quizId: string) => {
    if (!confirm("Are you sure you want to delete this quiz?")) return;

    try {
      await quizService.deleteQuiz(quizId);
      toast.success("Quiz deleted successfully");
      fetchQuizzes();
    } catch (error: any) {
      console.error("Error deleting quiz:", error);
      toast.error("Failed to delete quiz");
    }
  };

  const handleTogglePublish = async (quiz: Quiz) => {
    try {
      await quizService.togglePublishStatus(quiz._id!);
      toast.success(
        `Quiz ${quiz.isPublished ? "unpublished" : "published"} successfully`
      );
      fetchQuizzes();
    } catch (error: any) {
      console.error("Error toggling publish status:", error);
      toast.error("Failed to update quiz status");
    }
  };

  const handleDuplicateQuiz = async (quizId: string) => {
    try {
      await quizService.duplicateQuiz(quizId);
      toast.success("Quiz duplicated successfully");
      fetchQuizzes();
    } catch (error: any) {
      console.error("Error duplicating quiz:", error);
      toast.error("Failed to duplicate quiz");
    }
  };

  if (loading && quizzes.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">
          {userType === "teacher" ? "Your Quizzes" : "Available Quizzes"}
        </h2>
        {userType === "teacher" && (
          <Link
            to={`/teacher/quizzes/create${
              courseId ? `?courseId=${courseId}` : ""
            }`}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
          >
            + Create Quiz
          </Link>
        )}
      </div>

      {/* Search and Filters */}
      <div className="bg-white p-4 rounded-lg shadow">
        <form
          onSubmit={handleSearch}
          className="flex flex-col md:flex-row gap-4"
        >
          <div className="flex-1">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search quizzes..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {userType === "teacher" && (
            <div>
              <select
                value={statusFilter}
                onChange={(e) =>
                  setStatusFilter(
                    e.target.value as "all" | "published" | "draft" | "archived"
                  )
                }
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Status</option>
                <option value="published">Published</option>
                <option value="draft">Draft</option>
                <option value="archived">Archived</option>
              </select>
            </div>
          )}

          <button
            type="submit"
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            Search
          </button>
        </form>
      </div>

      {/* Quiz List */}
      <div className="space-y-4">
        {quizzes.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-400 text-6xl mb-4">📝</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {userType === "teacher"
                ? "No quizzes created yet"
                : "No quizzes available"}
            </h3>
            <p className="text-gray-600">
              {userType === "teacher"
                ? "Get started by creating your first quiz"
                : "Check back later for new quizzes"}
            </p>
          </div>
        ) : (
          quizzes.map((quiz) => (
            <div
              key={quiz._id}
              className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {quiz.title}
                    </h3>
                    <AvailabilityStatus quiz={quiz} />
                    {userType === "teacher" && (
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full ${
                          quiz.isPublished
                            ? "bg-green-100 text-green-800"
                            : "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {quiz.isPublished ? "Published" : "Draft"}
                      </span>
                    )}
                  </div>

                  {quiz.description && (
                    <p className="text-gray-600 mb-3 line-clamp-2">
                      {quiz.description}
                    </p>
                  )}

                  <div className="flex items-center space-x-4 text-sm text-gray-500">
                    <span>📝 {quiz.questions.length} questions</span>
                    <span>⏱️ {quiz.timeLimit} minutes</span>
                    <span>🎯 {quiz.passingScore}% to pass</span>
                    <span>🔄 {quiz.attempts} attempt(s)</span>
                  </div>

                  {quiz.schedule?.availableFrom && (
                    <div className="mt-2 text-xs text-gray-500">
                      Available:{" "}
                      {new Date(quiz.schedule.availableFrom).toLocaleString()}
                      {quiz.schedule.dueDate && (
                        <span>
                          {" "}
                          • Due:{" "}
                          {new Date(quiz.schedule.dueDate).toLocaleString()}
                        </span>
                      )}
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center space-x-2 ml-4">
                  {userType === "teacher" ? (
                    <>
                      <Link
                        to={`/teacher/quizzes/${quiz._id}/edit`}
                        className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                      >
                        Edit
                      </Link>
                      <Link
                        to={`/teacher/quizzes/${quiz._id}/analytics`}
                        className="text-green-600 hover:text-green-700 text-sm font-medium"
                      >
                        Analytics
                      </Link>
                      <button
                        onClick={() => handleDuplicateQuiz(quiz._id!)}
                        className="text-purple-600 hover:text-purple-700 text-sm font-medium"
                      >
                        Duplicate
                      </button>
                      <button
                        onClick={() => handleTogglePublish(quiz)}
                        className={`text-sm font-medium ${
                          quiz.isPublished
                            ? "text-orange-600 hover:text-orange-700"
                            : "text-green-600 hover:text-green-700"
                        }`}
                      >
                        {quiz.isPublished ? "Unpublish" : "Publish"}
                      </button>
                      <button
                        onClick={() => handleDeleteQuiz(quiz._id!)}
                        className="text-red-600 hover:text-red-700 text-sm font-medium"
                      >
                        Delete
                      </button>
                    </>
                  ) : (
                    <Link
                      to={`/student/quizzes/${quiz._id}`}
                      className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                        quiz.isAvailable
                          ? "bg-blue-600 text-white hover:bg-blue-700"
                          : "bg-gray-300 text-gray-500 cursor-not-allowed"
                      }`}
                    >
                      {quiz.isAvailable ? "Take Quiz" : "Not Available"}
                    </Link>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center space-x-2">
          <button
            onClick={() => setPage(page - 1)}
            disabled={page === 1}
            className="px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>

          <span className="px-3 py-2 text-sm text-gray-700">
            Page {page} of {totalPages}
          </span>

          <button
            onClick={() => setPage(page + 1)}
            disabled={page === totalPages}
            className="px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default QuizDashboard;
